import React, { useState, useEffect, useRef } from 'react';
import { Button } from '../shadcn/button';
import { Input } from '../shadcn/input';
import { Edit3, Check, X as XIcon, Trash2 } from 'lucide-react';
import { createPortal } from 'react-dom';

// Simple skeleton component
const Skeleton = ({ className }) => (
  <div className={`bg-gray-200 rounded animate-pulse ${className}`}></div>
);

// Function to detect drivable area in an image
// Function to detect drivable area in an image using color and texture analysis
const detectDrivableArea = (imageData, width, height) => {
  // Create a copy of the image data for processing
  const data = new Uint8ClampedArray(imageData);
  
  // Initialize a mask for potential road pixels
  const roadMask = new Uint8ClampedArray(width * height);
  
  // Analyze each pixel for road-like characteristics
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const idx = (y * width + x) * 4;
      const r = data[idx];
      const g = data[idx + 1];
      const b = data[idx + 2];
      
      // Check if pixel has road-like color characteristics
      // Roads are typically gray, brown, or dark-colored
      const isRoadColor = isRoadLikeColor(r, g, b);
      
      // Check texture (roads are generally smooth)
      const isSmoothTexture = isSmoothArea(data, width, height, x, y);
      
      // Check position (roads are typically in the lower portion of the image)
      const isLowerPosition = y > height * 0.4;
      
      // If pixel meets road criteria, mark it in the mask
      if (isRoadColor && isSmoothTexture && isLowerPosition) {
        roadMask[y * width + x] = 255;
      }
    }
  }
  
  // Apply morphological operations to clean up the mask
  const cleanedMask = applyMorphologicalOperations(roadMask, width, height);
  
  // Find the largest connected component (likely the road)
  const roadComponent = findLargestConnectedComponent(cleanedMask, width, height);
  
  // Generate polygon from the road component
  const roadPolygon = generateRoadPolygon(roadComponent, width, height);
  
  return {
    polygon: roadPolygon
  };
};

// Check if a pixel has road-like color characteristics
const isRoadLikeColor = (r, g, b) => {
  // Convert to HSV-like analysis for better color detection
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const delta = max - min;
  
  // Calculate saturation (roads are typically low saturation)
  const saturation = max === 0 ? 0 : delta / max;
  
  // Roads are typically:
  // - Dark to medium brightness
  // - Low saturation (grayish, brownish, or dark colors)
  // - Not too red or too blue
  
  const isDarkToMedium = max < 200;
  const isLowSaturation = saturation < 0.5;
  const isNotTooRed = !(r > b * 1.3 && r > g * 1.2);
  const isNotTooBlue = !(b > r * 1.3 && b > g * 1.1);
  
  return isDarkToMedium && isLowSaturation && isNotTooRed && isNotTooBlue;
};

// Check if an area is smooth (roads are generally smooth)
const isSmoothArea = (data, width, height, x, y) => {
  if (x < 2 || x >= width - 2 || y < 2 || y >= height - 2) {
    return false;
  }
  
  // Calculate local variance as a measure of texture
  let sum = 0;
  let sumSquares = 0;
  let count = 0;
  
  // Sample a 5x5 neighborhood
  for (let dy = -2; dy <= 2; dy++) {
    for (let dx = -2; dx <= 2; dx++) {
      const nx = x + dx;
      const ny = y + dy;
      if (nx >= 0 && nx < width && ny >= 0 && ny < height) {
        const idx = (ny * width + nx) * 4;
        const gray = 0.299 * data[idx] + 0.587 * data[idx + 1] + 0.114 * data[idx + 2];
        sum += gray;
        sumSquares += gray * gray;
        count++;
      }
    }
  }
  
  if (count === 0) return false;
  
  const mean = sum / count;
  const variance = sumSquares / count - mean * mean;
  
  // Roads are generally smooth (low variance)
  return variance < 300;
};

// Apply morphological operations to clean up the mask
const applyMorphologicalOperations = (mask, width, height) => {
  const result = new Uint8ClampedArray(mask);
  
  // Apply erosion to remove noise
  for (let y = 1; y < height - 1; y++) {
    for (let x = 1; x < width - 1; x++) {
      if (mask[y * width + x] === 255) {
        // Check if all neighbors are also road pixels
        let allNeighborsRoad = true;
        for (let dy = -1; dy <= 1; dy++) {
          for (let dx = -1; dx <= 1; dx++) {
            if (mask[(y + dy) * width + (x + dx)] !== 255) {
              allNeighborsRoad = false;
              break;
            }
          }
          if (!allNeighborsRoad) break;
        }
        
        if (!allNeighborsRoad) {
          result[y * width + x] = 0;
        }
      }
    }
  }
  
  // Apply dilation to fill gaps
  const temp = new Uint8ClampedArray(result);
  for (let y = 1; y < height - 1; y++) {
    for (let x = 1; x < width - 1; x++) {
      if (result[y * width + x] === 0) {
        // Check if any neighbor is a road pixel
        let anyNeighborRoad = false;
        for (let dy = -1; dy <= 1; dy++) {
          for (let dx = -1; dx <= 1; dx++) {
            if (result[(y + dy) * width + (x + dx)] === 255) {
              anyNeighborRoad = true;
              break;
            }
          }
          if (anyNeighborRoad) break;
        }
        
        if (anyNeighborRoad) {
          temp[y * width + x] = 255;
        }
      }
    }
  }
  
  return temp;
};

// Find the largest connected component
const findLargestConnectedComponent = (mask, width, height) => {
  const visited = new Uint8ClampedArray(width * height);
  let largestComponent = [];
  let maxSize = 0;
  
  // Flood fill to find connected components
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const idx = y * width + x;
      if (mask[idx] === 255 && visited[idx] === 0) {
        const component = [];
        const queue = [[x, y]];
        visited[idx] = 1;
        
        while (queue.length > 0) {
          const [cx, cy] = queue.shift();
          component.push([cx, cy]);
          
          // Check 4-connected neighbors
          const neighbors = [
            [cx - 1, cy], [cx + 1, cy],
            [cx, cy - 1], [cx, cy + 1]
          ];
          
          for (const [nx, ny] of neighbors) {
            if (nx >= 0 && nx < width && ny >= 0 && ny < height) {
              const nidx = ny * width + nx;
              if (mask[nidx] === 255 && visited[nidx] === 0) {
                visited[nidx] = 1;
                queue.push([nx, ny]);
              }
            }
          }
        }
        
        if (component.length > maxSize) {
          maxSize = component.length;
          largestComponent = component;
        }
      }
    }
  }
  
  return largestComponent;
};

// Generate polygon from road component
const generateRoadPolygon = (component, width, height) => {
  if (component.length === 0) {
    // Return a default polygon if no road detected
    return [
      { x: width * 0.2, y: height * 0.6 },
      { x: width * 0.8, y: height * 0.6 },
      { x: width * 0.9, y: height * 0.9 },
      { x: width * 0.1, y: height * 0.9 }
    ];
  }
  
  // Find boundary points of the component
  let minX = width, maxX = 0, minY = height, maxY = 0;
  
  for (const [x, y] of component) {
    minX = Math.min(minX, x);
    maxX = Math.max(maxX, x);
    minY = Math.min(minY, y);
    maxY = Math.max(maxY, y);
  }
  
  // Create a more complex polygon with at least 6 points
  const centerX = (minX + maxX) / 2;
  const centerY = (minY + maxY) / 2;
  
  // Adjust for perspective (narrower at the top)
  const topWidth = (maxX - minX) * 0.7;
  const bottomWidth = maxX - minX;
  
  return [
    { x: centerX - topWidth / 2, y: minY }, // Top left
    { x: centerX + topWidth / 2, y: minY }, // Top right
    { x: maxX, y: centerY }, // Right middle
    { x: centerX + bottomWidth / 2, y: maxY }, // Bottom right
    { x: centerX - bottomWidth / 2, y: maxY }, // Bottom left
    { x: minX, y: centerY } // Left middle
  ];
};

// Apply Gaussian blur

// Apply Gaussian blur
const applyGaussianBlur = (data, width, height) => {
  const output = new Uint8ClampedArray(data.length);
  const kernel = [
    [1, 4, 7, 4, 1],
    [4, 16, 26, 16, 4],
    [7, 26, 41, 26, 7],
    [4, 16, 26, 16, 4],
    [1, 4, 7, 4, 1]
  ];
  const kernelWeight = 273;
  
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      let sum = 0;
      for (let ky = -2; ky <= 2; ky++) {
        for (let kx = -2; kx <= 2; kx++) {
          const nx = Math.min(width - 1, Math.max(0, x + kx));
          const ny = Math.min(height - 1, Math.max(0, y + ky));
          const idx = (ny * width + nx) * 4;
          sum += data[idx] * kernel[ky + 2][kx + 2];
        }
      }
      const idx = (y * width + x) * 4;
      const blurred = Math.round(sum / kernelWeight);
      output[idx] = output[idx + 1] = output[idx + 2] = blurred;
      output[idx + 3] = data[idx + 3]; // Preserve alpha
    }
  }
  
  return output;
};

// Canny edge detection
const cannyEdgeDetection = (data, width, height, lowThreshold, highThreshold) => {
  // Simplified Canny edge detection - in a real implementation, this would be more complex
  const edges = new Uint8ClampedArray(data.length);
  
  // Simple edge detection using gradient magnitude
  for (let y = 1; y < height - 1; y++) {
    for (let x = 1; x < width - 1; x++) {
      const idx = (y * width + x) * 4;
      
      // Calculate gradients
      const gx = (
        -data[((y - 1) * width + (x - 1)) * 4] +
        data[((y - 1) * width + (x + 1)) * 4] +
        -2 * data[(y * width + (x - 1)) * 4] +
        2 * data[(y * width + (x + 1)) * 4] +
        -data[((y + 1) * width + (x - 1)) * 4] +
        data[((y + 1) * width + (x + 1)) * 4]
      );
      
      const gy = (
        data[((y - 1) * width + (x - 1)) * 4] +
        2 * data[((y - 1) * width + x) * 4] +
        data[((y - 1) * width + (x + 1)) * 4] +
        -data[((y + 1) * width + (x - 1)) * 4] +
        -2 * data[((y + 1) * width + x) * 4] +
        -data[((y + 1) * width + (x + 1)) * 4]
      );
      
      // Calculate gradient magnitude
      const magnitude = Math.sqrt(gx * gx + gy * gy);
      
      // Apply threshold
      if (magnitude > highThreshold) {
        edges[idx] = edges[idx + 1] = edges[idx + 2] = 255;
      } else if (magnitude > lowThreshold) {
        edges[idx] = edges[idx + 1] = edges[idx + 2] = 128;
      } else {
        edges[idx] = edges[idx + 1] = edges[idx + 2] = 0;
      }
      edges[idx + 3] = 255; // Alpha
    }
  }
  
  return edges;
};

// Hough transform for line detection
const houghTransform = (edges, width, height, rhoResolution, thetaResolution, threshold) => {
  const diagonal = Math.sqrt(width * width + height * height);
  const rhoMax = Math.ceil(diagonal);
  const thetaMax = Math.ceil(Math.PI / thetaResolution);
  
  // Initialize accumulator array
  const accumulator = new Array(2 * rhoMax + 1);
  for (let i = 0; i < accumulator.length; i++) {
    accumulator[i] = new Array(thetaMax).fill(0);
  }
  
  // Populate accumulator
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const idx = (y * width + x) * 4;
      if (edges[idx] === 255) { // Edge pixel
        for (let t = 0; t < thetaMax; t++) {
          const theta = t * thetaResolution;
          const rho = Math.round(x * Math.cos(theta) + y * Math.sin(theta)) + rhoMax;
          if (rho >= 0 && rho < accumulator.length) {
            accumulator[rho][t]++;
          }
        }
      }
    }
  }
  
  // Extract lines above threshold
  const lines = [];
  for (let rho = 0; rho < accumulator.length; rho++) {
    for (let theta = 0; theta < thetaMax; theta++) {
      if (accumulator[rho][theta] >= threshold) {
        lines.push({
          rho: rho - rhoMax,
          theta: theta * thetaResolution
        });
      }
    }
  }
  
  return lines;
};

// Filter lines to find road lanes
const TimestampVerificationWidget = React.memo(({ 
  group, 
  getGroupTimestampRange, 
  getGroupTotalDuration, 
  formatDuration,
  manualTimestampRanges,
  setManualTimestampRanges,
  videoDurations,
  unifiedTimestamps,
  confirmedDurations,
  setConfirmedDurations,
  videoThumbnails,
  groupDirectionsSet,
  setGroupDirectionsSet,
  groupCardinalDirections,
  setGroupCardinalDirections,
  savedDrivableAreas,
  setSavedDrivableAreas
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editedRange, setEditedRange] = useState('');
  const [totalDuration, setTotalDuration] = useState(0);
  const [loadingDuration, setLoadingDuration] = useState(false);
  const [directionsSet, setDirectionsSet] = useState(false);
  const [showOverlay, setShowOverlay] = useState(false);
  const canvasRef = useRef(null);
  const imageRef = useRef(null);
  const [drivableArea, setDrivableArea] = useState(null);
  // Use the passed-in savedDrivableAreas and setSavedDrivableAreas instead of local state
  const [dragState, setDragState] = useState({
    isDragging: false,
    pointIndex: -1,
    startX: 0,
    startY: 0
  });
  const [cardinalDragState, setCardinalDragState] = useState({
    isDragging: false,
    direction: null,
    offsetX: 0,
    offsetY: 0
  });
  const [isDraggingOverBin, setIsDraggingOverBin] = useState(false);
  const binRef = useRef(null);

  // Get the current timestamp range
  const currentRange = getGroupTimestampRange(group, manualTimestampRanges, unifiedTimestamps, videoDurations);
  
  // Check if duration is confirmed for this group
  const isDurationConfirmed = confirmedDurations[group.id] || false;
  
  // Toggle duration confirmation
  const toggleDurationConfirmation = () => {
    setConfirmedDurations(prev => ({
      ...prev,
      [group.id]: !isDurationConfirmed
    }));
  };
  
  // State to track original values for cancel functionality
  const [originalDrivableArea, setOriginalDrivableArea] = useState(null);
  const [originalCardinalDirections, setOriginalCardinalDirections] = useState(null);

  // Toggle directions state
  const toggleDirections = () => {
    // Always open the overlay when the Set button is clicked
    // Set directionsSet based on whether this group has saved directions
    setDirectionsSet(!!(groupDirectionsSet && groupDirectionsSet[group.id]));
    // Save original values for potential cancel
    setOriginalDrivableArea(savedDrivableAreas[group.id] || null);
    setOriginalCardinalDirections(groupCardinalDirections[group.id] || null);
    setShowOverlay(true);
  };

  // Close overlay
  const closeOverlay = () => {
    setShowOverlay(false);
    setDragState({
      isDragging: false,
      pointIndex: -1,
      startX: 0,
      startY: 0
    });
  };

  // Function to clear all cardinal directions
  const clearAllCardinalDirections = () => {
    setGroupCardinalDirections(prev => {
      const newDirections = { ...prev };
      if (newDirections[group.id]) {
        delete newDirections[group.id];
      }
      return newDirections;
    });
  };

  // Cancel overlay (restore original state and close)
  const cancelOverlay = () => {
    // Restore original drivable area if it was changed
    if (originalDrivableArea !== undefined && originalDrivableArea !== null) {
      // We need to update the saved drivable area to the original
      setSavedDrivableAreas(prev => ({
        ...prev,
        [group.id]: originalDrivableArea
      }));
    } else {
      // If there was no original, remove the area
      setSavedDrivableAreas(prev => {
        const newAreas = { ...prev };
        delete newAreas[group.id];
        return newAreas;
      });
    }

    // Restore original cardinal directions
    if (originalCardinalDirections !== undefined && originalCardinalDirections !== null) {
      setGroupCardinalDirections(prev => ({
        ...prev,
        [group.id]: originalCardinalDirections
      }));
    } else {
      // If there were no original directions, remove the group's directions
      setGroupCardinalDirections(prev => {
        const newDirections = { ...prev };
        if (newDirections[group.id]) {
          delete newDirections[group.id];
        }
        return newDirections;
      });
    }

    closeOverlay();
  };
  
  // Load total duration only when group changes
  useEffect(() => {
    // Don't calculate if group is empty
    if (!group || !group.files || group.files.length === 0) {
      setTotalDuration(0);
      return;
    }
    
    const loadDuration = async () => {
      setLoadingDuration(true);
      try {
        const duration = await getGroupTotalDuration(group);
        setTotalDuration(duration);
      } catch (error) {
        // Removed console.error for production
      } finally {
        setLoadingDuration(false);
      }
    };
    
    // Only calculate if we don't already have the duration cached
    const hasAllDurations = group.files.every(file => videoDurations[file.id]);
    if (!hasAllDurations) {
      loadDuration();
    } else {
      // Use cached durations
      const cachedDuration = group.files.reduce((total, file) => {
        return total + (videoDurations[file.id] || 0);
      }, 0);
      setTotalDuration(cachedDuration);
      setLoadingDuration(false); // Ensure loading state is false
    }
  }, [group.id]); // Simplified dependencies

  const handleEdit = () => {
    setEditedRange(currentRange);
    setIsEditing(true);
  };

  const handleSave = () => {
    // Validate the edited range
    const validationError = validateTimestampRange(editedRange);
    if (validationError) {
      // In a real implementation, you might want to show this error to the user
      // Removed console.error for production
      // For now, we'll just not save the invalid value
      // You might want to show an error message to the user instead
      return;
    }
    
    setManualTimestampRanges(prev => ({
      ...prev,
      [group.id]: editedRange
    }));
    setIsEditing(false);
  };

  // Helper function to validate timestamp range
  const validateTimestampRange = (range) => {
    // Check if range is empty
    if (!range || range.trim() === '') {
      return 'Timestamp range cannot be empty';
    }
    
    // Check format: YYYY-MM-DD HH:MM to YYYY-MM-DD HH:MM
    const formatRegex = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2} to \d{4}-\d{2}-\d{2} \d{2}:\d{2}$/;
    if (!formatRegex.test(range)) {
      return 'Invalid format. Expected: YYYY-MM-DD HH:MM to YYYY-MM-DD HH:MM';
    }
    
    // Parse the timestamps
    const [startStr, endStr] = range.split(' to ');
    const [startDatePart, startTimePart] = startStr.split(' ');
    const [endDatePart, endTimePart] = endStr.split(' ');
    
    // Parse start date
    const [startYear, startMonth, startDay] = startDatePart.split('-').map(Number);
    const [startHour, startMinute] = startTimePart.split(':').map(Number);
    const startDate = new Date(startYear, startMonth - 1, startDay, startHour, startMinute);
    
    // Parse end date
    const [endYear, endMonth, endDay] = endDatePart.split('-').map(Number);
    const [endHour, endMinute] = endTimePart.split(':').map(Number);
    const endDate = new Date(endYear, endMonth - 1, endDay, endHour, endMinute);
    
    // Check if dates are valid
    if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
      return 'Invalid date values';
    }
    
    // Check if start date is before end date
    if (startDate >= endDate) {
      return 'Start date must be before end date';
    }
    
    // Check if start date is before 1960
    const minDate = new Date(1960, 0, 1); // January 1, 1960
    if (startDate < minDate) {
      return 'Start date cannot be before 1960';
    }
    
    // Check if end date is after tomorrow
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    tomorrow.setHours(23, 59, 59, 999); // End of tomorrow
    
    if (endDate > tomorrow) {
      return 'End date cannot be after tomorrow';
    }
    
    // All validations passed
    return null;
  };

  const handleCancel = () => {
    setEditedRange('');
    setIsEditing(false);
  };

  // Process image for drivable area detection
  const processImageForRoadDetection = () => {
    if (imageRef.current && canvasRef.current) {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      const image = imageRef.current;
      
      // Set canvas dimensions to match image
      canvas.width = image.naturalWidth;
      canvas.height = image.naturalHeight;
            
      // Draw image on canvas
      ctx.drawImage(image, 0, 0);
      
      // Use saved drivable area for this specific group if it exists, otherwise detect
      if (savedDrivableAreas && savedDrivableAreas[group.id]) {
        // Denormalize coordinates from saved points (0-1 range) to actual image coordinates
        const denormalizedArea = savedDrivableAreas[group.id].map(point => ({
          x: point.x * image.naturalWidth,
          y: point.y * image.naturalHeight
        }));
        setDrivableArea(denormalizedArea);
      } else {
        // Get image data
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        
        // Perform drivable area detection
        const detectionResult = detectDrivableArea(
          imageData.data,
          imageData.width,
          imageData.height
        );        
        // Store the drivable area for interaction
        setDrivableArea(detectionResult.polygon);
      }
    }
  };

  // Handle mouse down on cardinal direction button
  const handleCardinalMouseDown = (e, direction) => {
    e.preventDefault();
    e.stopPropagation(); // Stop event from bubbling up
    
    // Add temporary body styling to prevent text selection during drag
    document.body.style.userSelect = 'none';
    document.body.style.webkitUserSelect = 'none';
    
    setCardinalDragState({
      isDragging: true,
      direction,
      offsetX: e.clientX,
      offsetY: e.clientY
    });
  };

  // Handle mouse move for cardinal direction dragging
  const handleCardinalMouseMove = (e) => {
    // Only handle global mouse move when dragging a cardinal direction from the button
    // (not when repositioning an existing cardinal direction on the canvas)
    if (!cardinalDragState.isDragging || dragState.isDragging) {
      return;
    }
    
    e.preventDefault();
    e.stopPropagation(); // Stop event from bubbling up
    
    // Update the visual indicator position
    setCardinalDragState(prev => ({
      ...prev,
      offsetX: e.clientX,
      offsetY: e.clientY
    }));
    
    // Check if dragging over the bin
    const binElement = binRef.current;
    let isOverBin = false;

    if (binElement) {
      const binRect = binElement.getBoundingClientRect();
      isOverBin = (
        e.clientX >= binRect.left &&
        e.clientX <= binRect.right &&
        e.clientY >= binRect.top &&
        e.clientY <= binRect.bottom
      );
    }

    setIsDraggingOverBin(isOverBin);
  };

  // Handle mouse up for cardinal direction dropping
  const handleCardinalMouseUp = (e) => {
    // Only handle global mouse up when dragging a cardinal direction from the button
    // (not when repositioning an existing cardinal direction on the canvas)
    if (!cardinalDragState.isDragging || dragState.isDragging) {
      return;
    }
    
    // Clean up body styling
    document.body.style.userSelect = '';
    document.body.style.webkitUserSelect = '';
    
    // Reset dragging over bin state
    setIsDraggingOverBin(false);
    
    // Check if dropped on canvas
    const canvas = canvasRef.current;
    const image = imageRef.current;
    if (canvas && image) {
      const rect = canvas.getBoundingClientRect();
      const isOnCanvas = (
        e.clientX >= rect.left &&
        e.clientX <= rect.right &&
        e.clientY >= rect.top &&
        e.clientY <= rect.bottom
      );
      
      // Check if dropped on the bin icon
      const binElement = binRef.current;
      let isDroppedOnBin = false;

      if (binElement) {
        const binRect = binElement.getBoundingClientRect();
        isDroppedOnBin = (
          e.clientX >= binRect.left &&
          e.clientX <= binRect.right &&
          e.clientY >= binRect.top &&
          e.clientY <= binRect.bottom
        );
      }
            
      if (isDroppedOnBin) {
        // If dropped on the bin, remove the cardinal direction
        setGroupCardinalDirections(prev => {
          const newDirections = { ...prev };
          if (newDirections[group.id]) {
            const groupDirections = { ...newDirections[group.id] };
            delete groupDirections[cardinalDragState.direction.toLowerCase()];
            newDirections[group.id] = groupDirections;
          }
          return newDirections;
        });
        
        // Show visual feedback
      } else if (isOnCanvas) {
        // Calculate position relative to canvas
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;
        
        // Convert to canvas coordinates (accounting for scaling)
        const scaleX = canvas.width / canvas.clientWidth;
        const scaleY = canvas.height / canvas.clientHeight;
        const canvasX = x * scaleX;
        const canvasY = y * scaleY;
        
        
        // Convert to relative coordinates based on image dimensions
        const imageWidth = image.naturalWidth;
        const imageHeight = image.naturalHeight;
        const relativeX = canvasX / imageWidth;
        const relativeY = canvasY / imageHeight;
        
        // Store the cardinal direction position as relative coordinates
        setGroupCardinalDirections(prev => ({
          ...prev,
          [group.id]: {
            ...prev[group.id],
            [cardinalDragState.direction.toLowerCase()]: { x: relativeX, y: relativeY }
          }
        }));
      }
    }
    
    setCardinalDragState({
      isDragging: false,
      direction: null,
      offsetX: 0,
      offsetY: 0
    });
    
    // Prevent event from bubbling to avoid interference with other handlers
    e.preventDefault();
    e.stopPropagation();
  };

  // Handle mouse down on canvas for point selection (polygon points and cardinal directions)
  const handleMouseDown = (e) => {
    if (!drivableArea) {
      return;
    }
    
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const mouseX = e.clientX - rect.left;
    const mouseY = e.clientY - rect.top;
    
    // Calculate scale factors to convert from display coordinates to canvas coordinates
    const scaleX = canvas.width / canvas.clientWidth;
    const scaleY = canvas.height / canvas.clientHeight;
    
    // Convert mouse coordinates to canvas coordinate system
    const canvasX = mouseX * scaleX;
    const canvasY = mouseY * scaleY;
    
    // First check if clicked near any cardinal direction
    if (groupCardinalDirections && groupCardinalDirections[group.id]) {
      const directions = groupCardinalDirections[group.id];
      
      // Get image dimensions for coordinate conversion
      const image = imageRef.current;
      if (image) {
        const imageWidth = image.naturalWidth;
        const imageHeight = image.naturalHeight;
        
        for (const [direction, position] of Object.entries(directions)) {
          // Skip checking if position is null/undefined
          if (!position) {
            continue;
          }
          
          // Only check if position is a valid object with x and y coordinates
          if (typeof position === 'object' && position.x !== undefined && position.y !== undefined) {
            // Convert relative coordinates to canvas coordinates
            const x = position.x * imageWidth;
            const y = position.y * imageHeight;
            
            // Calculate distance
            const deltaX = canvasX - x;
            const deltaY = canvasY - y;
            const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);
                        
            if (distance < 20) { // 20px tolerance for easier selection
              // Set cardinal drag state to allow repositioning
              setCardinalDragState({
                isDragging: true,
                direction,
                offsetX: e.clientX,
                offsetY: e.clientY
              });
              
              // Add temporary body styling to prevent text selection during drag
              document.body.style.userSelect = 'none';
              document.body.style.webkitUserSelect = 'none';
              
              e.preventDefault();
              e.stopPropagation();
              return;
            }
          }
        }
      }
    }
    
    // Check if clicked near any polygon point
    for (let i = 0; i < drivableArea.length; i++) {
      const point = drivableArea[i];
      
      // Calculate distance
      const deltaX = canvasX - point.x;
      const deltaY = canvasY - point.y;
      const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);
            
      if (distance < 20) { // 20px tolerance for easier selection
        setDragState({
          isDragging: true,
          pointIndex: i,
          startX: canvasX,
          startY: canvasY
        });
        e.preventDefault();
        e.stopPropagation();
        return;
      }
    }
  };

  // Handle double click to add a point on a line
  const handleDoubleClick = (e) => {
    if (!drivableArea) return;
    
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const mouseX = e.clientX - rect.left;
    const mouseY = e.clientY - rect.top;
    
    // Calculate scale factors to convert from display coordinates to canvas coordinates
    const scaleX = canvas.width / canvas.clientWidth;
    const scaleY = canvas.height / canvas.clientHeight;
    
    // Convert mouse coordinates to canvas coordinate system
    const canvasX = mouseX * scaleX;
    const canvasY = mouseY * scaleY;
    
    // Find the closest line segment
    let minDistance = Infinity;
    let insertIndex = -1;
    
    // Check each line segment
    for (let i = 0; i < drivableArea.length; i++) {
      const p1 = drivableArea[i];
      const p2 = drivableArea[(i + 1) % drivableArea.length]; // Wrap around to first point
      
      // Calculate distance from point to line segment
      const distance = distanceToLineSegment(canvasX, canvasY, p1.x, p1.y, p2.x, p2.y);
      
      if (distance < minDistance && distance < 20) { // 20px threshold
        minDistance = distance;
        insertIndex = i + 1; // Insert after the first point of the segment
      }
    }
    
    // If we found a line segment close enough, add a new point
    if (insertIndex !== -1) {
      const newPoint = { x: canvasX, y: canvasY };
      const updatedArea = [...drivableArea];
      updatedArea.splice(insertIndex, 0, newPoint);
      setDrivableArea(updatedArea);
      e.preventDefault();
      e.stopPropagation();
    }
  };

  // Calculate distance from a point to a line segment
  const distanceToLineSegment = (px, py, x1, y1, x2, y2) => {
    // Vector from point 1 to point 2
    const dx = x2 - x1;
    const dy = y2 - y1;
    
    // Length squared of the line segment
    const lengthSq = dx * dx + dy * dy;
    
    // If the line segment has zero length, return distance to either point
    if (lengthSq === 0) {
      return Math.sqrt((px - x1) * (px - x1) + (py - y1) * (py - y1));
    }
    
    // Calculate the projection of point P onto the line
    let t = ((px - x1) * dx + (py - y1) * dy) / lengthSq;
    
    // Clamp t to the range [0, 1] to stay within the line segment
    t = Math.max(0, Math.min(1, t));
    
    // Calculate the closest point on the line segment
    const closestX = x1 + t * dx;
    const closestY = y1 + t * dy;
    
    // Return the distance from P to the closest point
    return Math.sqrt((px - closestX) * (px - closestX) + (py - closestY) * (py - closestY));
  };

  // Get color for cardinal direction
  const getDirectionColor = (direction) => {
    switch (direction?.toLowerCase()) {
      case 'north': return '#dc2626'; // Professional dark red (Tailwind red-600)
      case 'east': return '#16a34a';  // Professional dark green (Tailwind green-600)
      case 'south': return '#2563eb'; // Professional dark blue (Tailwind blue-600)
      case 'west': return '#ca8a04';  // Professional dark yellow/gold (Tailwind yellow-600)
      default: return '#4b5563';      // Professional gray (Tailwind gray-600)
    }
  };

  // Handle mouse move on canvas for dragging points and cardinal directions
  const handleMouseMove = (e) => {
    // Handle polygon point dragging
    if (dragState.isDragging && dragState.pointIndex !== -1 && drivableArea) {
      
      const canvas = canvasRef.current;
      const rect = canvas.getBoundingClientRect();
      const mouseX = e.clientX - rect.left;
      const mouseY = e.clientY - rect.top;
      
      // Calculate scale factors to convert from display coordinates to canvas coordinates
      const scaleX = canvas.width / canvas.clientWidth;
      const scaleY = canvas.height / canvas.clientHeight;
      
      // Convert mouse coordinates to canvas coordinate system
      const canvasX = mouseX * scaleX;
      const canvasY = mouseY * scaleY;
      
      // Constrain to canvas bounds
      const constrainedX = Math.max(0, Math.min(canvas.width, canvasX));
      const constrainedY = Math.max(0, Math.min(canvas.height, canvasY));
      
      // Update the point position
      const updatedArea = [...drivableArea];
      updatedArea[dragState.pointIndex] = { 
        x: constrainedX, 
        y: constrainedY 
      };
      
      setDrivableArea(updatedArea);
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    
    // Handle cardinal direction dragging
    if (cardinalDragState.isDragging && cardinalDragState.direction) {
      // Update the visual indicator position
      setCardinalDragState(prev => ({
        ...prev,
        offsetX: e.clientX,
        offsetY: e.clientY
      }));

      // Check if dragging over the bin
      const binElement = binRef.current;
      let isOverBin = false;

      if (binElement) {
        const binRect = binElement.getBoundingClientRect();
        isOverBin = (
          e.clientX >= binRect.left &&
          e.clientX <= binRect.right &&
          e.clientY >= binRect.top &&
          e.clientY <= binRect.bottom
        );
      }

      setIsDraggingOverBin(isOverBin);
      e.preventDefault();
      e.stopPropagation();
      return;
    }
  };

  // Handle mouse up to stop dragging points and cardinal directions
  const handleMouseUp = (e) => {    
    // Handle polygon point dragging release
    if (dragState.isDragging) {
      setDragState({
        isDragging: false,
        pointIndex: -1,
        startX: 0,
        startY: 0
      });
    }
    
    // Handle cardinal direction dragging release
    if (cardinalDragState.isDragging) {      
      // Clean up body styling
      document.body.style.userSelect = '';
      document.body.style.webkitUserSelect = '';
      
      // Reset dragging over bin state
      setIsDraggingOverBin(false);
      
      // Check if dropped on canvas
      const canvas = canvasRef.current;
      const image = imageRef.current;
      if (canvas && image) {
        const rect = canvas.getBoundingClientRect();
        const isOnCanvas = (
          e.clientX >= rect.left &&
          e.clientX <= rect.right &&
          e.clientY >= rect.top &&
          e.clientY <= rect.bottom
        );
        
        // Check if dropped on the bin icon
        const binElement = binRef.current;
        let isDroppedOnBin = false;

        if (binElement) {
          const binRect = binElement.getBoundingClientRect();
          isDroppedOnBin = (
            e.clientX >= binRect.left &&
            e.clientX <= binRect.right &&
            e.clientY >= binRect.top &&
            e.clientY <= binRect.bottom
          );
        }
        
        if (isDroppedOnBin) {
          // If dropped on the bin, remove the cardinal direction
          setGroupCardinalDirections(prev => {
            const newDirections = { ...prev };
            if (newDirections[group.id]) {
              const groupDirections = { ...newDirections[group.id] };
              delete groupDirections[cardinalDragState.direction.toLowerCase()];
              newDirections[group.id] = groupDirections;
            }
            return newDirections;
          });
          
          // Show visual feedback
        } else if (isOnCanvas) {
          // Calculate position relative to canvas
          const x = e.clientX - rect.left;
          const y = e.clientY - rect.top;
          
          // Convert to canvas coordinates (accounting for scaling)
          const scaleX = canvas.width / canvas.clientWidth;
          const scaleY = canvas.height / canvas.clientHeight;
          const canvasX = x * scaleX;
          const canvasY = y * scaleY;
          
          // Convert to relative coordinates based on image dimensions
          const imageWidth = image.naturalWidth;
          const imageHeight = image.naturalHeight;
          const relativeX = canvasX / imageWidth;
          const relativeY = canvasY / imageHeight;
          
          // Update the cardinal direction position as relative coordinates
          setGroupCardinalDirections(prev => ({
            ...prev,
            [group.id]: {
              ...prev[group.id],
              [cardinalDragState.direction.toLowerCase()]: { x: relativeX, y: relativeY }
            }
          }));
        }
      }
      
      setCardinalDragState({
        isDragging: false,
        direction: null,
        offsetX: 0,
        offsetY: 0
      });
    }
    
    e.stopPropagation();
  };

  // Handle mouse leave to stop dragging
  const handleMouseLeave = (e) => {
    if (dragState.isDragging) {
      setDragState({
        isDragging: false,
        pointIndex: -1,
        startX: 0,
        startY: 0
      });
    }
  };

  // Redraw the canvas with the current drivable area
  const redrawCanvas = () => {
    if (!canvasRef.current || !imageRef.current || !drivableArea) {
      return;
    }
    
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const image = imageRef.current;
    
    // Ensure canvas has correct dimensions
    if (canvas.width !== image.naturalWidth || canvas.height !== image.naturalHeight) {
      canvas.width = image.naturalWidth;
      canvas.height = image.naturalHeight;
    }
    
    // Clear canvas and redraw image
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(image, 0, 0);
    
    // Draw the drivable area polygon
    if (drivableArea.length >= 4) {
      // Draw filled polygon
      ctx.fillStyle = 'rgba(255, 255, 0, 0.3)';
      ctx.beginPath();
      ctx.moveTo(drivableArea[0].x, drivableArea[0].y);
      
      for (let i = 1; i < drivableArea.length; i++) {
        ctx.lineTo(drivableArea[i].x, drivableArea[i].y);
      }
      
      ctx.closePath();
      ctx.fill();
      
      // Draw polygon outline
      ctx.strokeStyle = 'rgba(255, 255, 0, 0.8)';
      ctx.lineWidth = 2;
      ctx.stroke();
      
      // Draw midpoints on line segments to indicate where points can be added
      ctx.fillStyle = 'rgba(0, 255, 255, 0.7)'; // Cyan color for add indicators
      for (let i = 0; i < drivableArea.length; i++) {
        const p1 = drivableArea[i];
        const p2 = drivableArea[(i + 1) % drivableArea.length]; // Wrap around to first point
        
        // Calculate midpoint
        const midX = (p1.x + p2.x) / 2;
        const midY = (p1.y + p2.y) / 2;
        
        // Draw small circle at midpoint
        ctx.beginPath();
        ctx.arc(midX, midY, 6, 0, Math.PI * 2);
        ctx.fill();
      }
      
      // Draw control points
      drivableArea.forEach((point, index) => {
        // Draw hit area for debugging (red transparent circle)
        ctx.beginPath();
        ctx.arc(point.x, point.y, 20, 0, Math.PI * 2);
        ctx.fillStyle = 'rgba(255, 0, 0, 0.2)'; // Red with low opacity for hit area
        ctx.fill();
        
        // Draw the actual control point
        ctx.beginPath();
        const radius = dragState.pointIndex === index ? 12 : 10;
        ctx.arc(point.x, point.y, radius, 0, Math.PI * 2);
        
        // Fill color - red if being dragged, green otherwise
        ctx.fillStyle = dragState.pointIndex === index ? '#ff0000' : '#00ff00';
        ctx.fill();
        
        // White border
        ctx.strokeStyle = '#ffffff';
        ctx.lineWidth = 2;
        ctx.stroke();
      });
    }
    
    // Draw cardinal directions if they exist for this group
    if (groupCardinalDirections && groupCardinalDirections[group.id]) {
      const directions = groupCardinalDirections[group.id];
      const directionColors = {
        north: '#dc2626', // Professional dark red (Tailwind red-600)
        east: '#16a34a',  // Professional dark green (Tailwind green-600)
        south: '#2563eb', // Professional dark blue (Tailwind blue-600)
        west: '#ca8a04'   // Professional dark yellow/gold (Tailwind yellow-600)
      };
      
      // Get image dimensions for coordinate conversion
      const image = imageRef.current;
      if (!image) {
        return;
      }
      
      const imageWidth = image.naturalWidth;
      const imageHeight = image.naturalHeight;
      
      Object.entries(directions).forEach(([direction, position]) => {
        // Skip drawing if position is null/undefined
        if (!position) {
          return;
        }
        
        // Only draw if position is a valid object with x and y coordinates
        if (typeof position === 'object' && position.x !== undefined && position.y !== undefined) {
          // Convert relative coordinates to canvas coordinates
          const canvasX = position.x * imageWidth;
          const canvasY = position.y * imageHeight;

          const color = directionColors[direction] || '#4b5563'; // Professional gray (Tailwind gray-600)
          
          // Draw direction marker (larger circle)
          ctx.beginPath();
          ctx.arc(canvasX, canvasY, 15, 0, Math.PI * 2);
          ctx.fillStyle = color;
          ctx.fill();
          ctx.strokeStyle = '#1f2937'; // Dark border for better visibility (Tailwind gray-800)
          ctx.lineWidth = 2;
          ctx.stroke();
          
          // Draw direction label (larger, bold text) with white color for better contrast
          ctx.font = 'bold 16px Arial';
          ctx.fillStyle = '#ffffff'; // White text for better contrast
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          ctx.fillText(direction.charAt(0).toUpperCase(), canvasX, canvasY);
        }
      });
    }
    
    // Draw visual indicator for dragging cardinal direction
    if (cardinalDragState.isDragging) {
      const rect = canvas.getBoundingClientRect();
      const scaleX = canvas.width / canvas.clientWidth;
      const scaleY = canvas.height / canvas.clientHeight;
      const canvasX = (cardinalDragState.offsetX - rect.left) * scaleX;
      const canvasY = (cardinalDragState.offsetY - rect.top) * scaleY;
      
      // Draw direction marker at current position (larger circle)
      ctx.beginPath();
      ctx.arc(canvasX, canvasY, 15, 0, Math.PI * 2);
      ctx.fillStyle = getDirectionColor(cardinalDragState.direction);
      ctx.fill();
      ctx.strokeStyle = '#1f2937'; // Dark border for better visibility (Tailwind gray-800)
      ctx.lineWidth = 2;
      ctx.stroke();
      
      // Draw direction label (larger, bold text) with white color for better contrast
      ctx.font = 'bold 16px Arial';
      ctx.fillStyle = '#ffffff'; // White text for better contrast
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(cardinalDragState.direction?.charAt(0), canvasX, canvasY);
    }
  };

  // Redraw canvas when drivable area or drag state changes
  useEffect(() => {
    if (showOverlay && drivableArea) {
      redrawCanvas();
    }
  }, [drivableArea, dragState, showOverlay, groupCardinalDirections]);

  // Ensure canvas is redrawn when it becomes visible
  useEffect(() => {
    if (showOverlay && drivableArea) {
      // Small delay to ensure DOM is ready
      const timer = setTimeout(() => {
        redrawCanvas();
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [showOverlay]);

  // Initialize drivable area when overlay opens
  useEffect(() => {
    if (showOverlay) {
      // Small delay to ensure image is loaded
      const timer = setTimeout(() => {
        processImageForRoadDetection();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [showOverlay]);

  // Attach event listeners directly to canvas element
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) {
      return;
    }
    
    // Remove existing React synthetic event handlers to prevent conflicts
    // Add native DOM event listeners for better control
    canvas.addEventListener('mousedown', handleMouseDown);
    canvas.addEventListener('mousemove', handleMouseMove);
    canvas.addEventListener('mouseup', handleMouseUp);
    canvas.addEventListener('mouseleave', handleMouseLeave);
    canvas.addEventListener('dblclick', handleDoubleClick);
    
    // Add global mouse listeners for cardinal direction dragging
    // Use capture phase to ensure we catch events even if they're stopped by other handlers
    document.addEventListener('mousemove', handleCardinalMouseMove, true);
    document.addEventListener('mouseup', handleCardinalMouseUp, true);
    
    return () => {
      canvas.removeEventListener('mousedown', handleMouseDown);
      canvas.removeEventListener('mousemove', handleMouseMove);
      canvas.removeEventListener('mouseup', handleMouseUp);
      canvas.removeEventListener('mouseleave', handleMouseLeave);
      canvas.removeEventListener('dblclick', handleDoubleClick);
      
      // Remove global mouse listeners
      document.removeEventListener('mousemove', handleCardinalMouseMove, true);
      document.removeEventListener('mouseup', handleCardinalMouseUp, true);
    };
  }, [drivableArea, dragState, cardinalDragState]); // Add cardinalDragState to dependencies

  // Handle image load
  const handleImageLoad = () => {
    processImageForRoadDetection();
  };

  // Create overlay portal
  const overlay = showOverlay ? createPortal(
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-[999999]">
      <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold">Road Drivable Area Adjustment</h3>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={closeOverlay}
            className="h-8 w-8 p-0"
          >
            <XIcon className="h-4 w-4" />
          </Button>
        </div>
        <div className="mb-4">
          <p className="text-sm text-gray-600 mb-2">
            <strong>Adjust the drivable area and set cardinal directions:</strong><br/>
            • Drag the green control points to adjust the drivable area boundaries<br/>
            • Double-click on any edge to add a new control point<br/>
            • Drag cardinal direction buttons (N/E/S/W) onto the image to mark specific locations<br/>
            • Drag existing direction markers to reposition them<br/>
            • Drop direction markers on the trash bin to remove them<br/>
            • Click the trash bin to remove all directions at once<br/>
            Displaying: {group.files && group.files.length > 0 ? group.files[0].file.name : 'video'}:
          </p>
          <div className="flex flex-col items-center">
            {/* Cardinal Direction Buttons - moved outside the image */}
            <div className="mb-4 w-full flex justify-center">
              <div className="flex flex-wrap gap-2 bg-white bg-opacity-80 p-2 rounded border">
                {[
                  { name: 'North', color: '#dc2626' }, // Professional dark red
                  { name: 'East', color: '#16a34a' },  // Professional dark green
                  { name: 'South', color: '#2563eb' }, // Professional dark blue
                  { name: 'West', color: '#ca8a04' }   // Professional dark yellow/gold
                ].map(({ name, color }) => (
                  <button
                    key={name}
                    className={`rounded font-bold cursor-move flex items-center justify-center ${
                      cardinalDragState.isDragging && cardinalDragState.direction === name
                        ? 'text-white border-2 border-gray-800'
                        : 'text-white hover:opacity-90'
                    }`}
                    style={{
                      width: '40px',
                      height: '40px',
                      fontSize: '16px',
                      backgroundColor: color
                    }}
                    onMouseDown={(e) => handleCardinalMouseDown(e, name)}
                  >
                    {name.charAt(0)}
                  </button>
                ))}
                {/* Bin Icon for removing directions */}
                <div
                  ref={binRef}
                  className={`rounded flex items-center justify-center cursor-pointer ${
                    isDraggingOverBin
                      ? 'bg-red-700 text-white'
                      : 'bg-red-500 text-white hover:bg-red-600'
                  }`}
                  style={{ width: '40px', height: '40px' }}
                  title="Click to remove all directions, or drop directions here to remove them"
                  onClick={clearAllCardinalDirections}
                >
                  <Trash2 size={20} />
                </div>
              </div>
            </div>

            {/* Visual indicator for dragging cardinal direction */}
            {cardinalDragState.isDragging && (
              <div
                className="fixed pointer-events-none z-[999999] flex items-center justify-center"
                style={{
                  left: `${cardinalDragState.offsetX}px`,
                  top: `${cardinalDragState.offsetY}px`,
                  transform: 'translate(-50%, -50%)',
                  width: '40px',
                  height: '40px',
                  borderRadius: '50%',
                  backgroundColor: getDirectionColor(cardinalDragState.direction),
                  fontWeight: 'bold',
                  fontSize: '16px',
                  boxShadow: '0 0 10px rgba(0,0,0,0.5)',
                  border: '2px solid #1f2937' // Dark border for better visibility
                }}
              >
                <span style={{ color: '#ffffff' }}>{cardinalDragState.direction?.charAt(0)}</span>
              </div>
            )}

            {/* Hidden image for processing */}
            <img
              ref={imageRef}
              src={group.files && group.files.length > 0 && videoThumbnails[group.files[0].id]
                ? videoThumbnails[group.files[0].id]
                : ''}
              alt="Video thumbnail"
              style={{ display: 'none' }}
              onLoad={handleImageLoad}
            />
            {/* Canvas for road detection visualization */}
            <div className="border rounded-lg overflow-hidden">
              <canvas
                ref={canvasRef}
                className="w-full h-auto"
                style={{
                  cursor: dragState.isDragging ? 'grabbing' : 'default',
                  touchAction: 'none' // Prevent touch scrolling on mobile
                }}
                draggable={false}
              />
            </div>
          </div>
        </div>
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={cancelOverlay}>
            Cancel
          </Button>
          <Button 
            onClick={() => {
              // Save the current drivable area with normalized coordinates
              if (drivableArea && imageRef.current) {
                const image = imageRef.current;
                const imageWidth = image.naturalWidth;
                const imageHeight = image.naturalHeight;
                
                // Normalize coordinates to be relative to image dimensions (0-1 range)
                const normalizedArea = drivableArea.map(point => ({
                  x: point.x / imageWidth,
                  y: point.y / imageHeight
                }));
                
                // Store saved points for this specific group using the passed-in setter
                setSavedDrivableAreas(prev => ({
                  ...prev,
                  [group.id]: normalizedArea
                }));
                
                // Mark directions as set for this group
                setGroupDirectionsSet(prev => ({
                  ...prev,
                  [group.id]: true
                }));
              }
              closeOverlay();
            }}
          >
            Save Detection
          </Button>
        </div>
      </div>
    </div>,
    document.body
  ) : null;

  return (
    <>
      <div className="mt-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
        <div className="flex flex-col sm:flex-row gap-3">
          {/* TIMESTAMP RANGE SECTION */}
          <div className="flex-1 min-w-0">
            <div className="text-xs font-semibold text-blue-800 mb-1">TIMESTAMP RANGE</div>
            {isEditing ? (
              <div className="flex items-center gap-2">
                <Input
                  type="text"
                  value={editedRange}
                  onChange={(e) => setEditedRange(e.target.value)}
                  className="text-sm py-1 px-2 h-8 flex-1 min-w-0"
                  placeholder="YYYY-MM-DD HH:MM to YYYY-MM-DD HH:MM"
                  disabled={true}
                />
                <div className="group relative">
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={handleSave}
                    className="h-8 w-8 p-0 flex-shrink-0 cursor-not-allowed opacity-50"
                    disabled={true}
                  >
                    <Check className="h-4 w-4 text-green-600" />
                  </Button>
                  <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 hidden group-hover:block bg-gray-800 text-white text-xs rounded py-1 px-2 z-10 whitespace-nowrap transition-opacity duration-200 opacity-0 group-hover:opacity-100">
                    Coming soon
                  </div>
                </div>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={handleCancel}
                  className="h-8 w-8 p-0 flex-shrink-0"
                >
                  <XIcon className="h-4 w-4 text-red-600" />
                </Button>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <span className="font-mono text-sm bg-white px-2 py-1 rounded border min-w-0 truncate flex-1 opacity-75">
                  {currentRange}
                </span>
                <div className="group relative">
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={handleEdit}
                    className="h-8 w-8 p-0 flex-shrink-0 cursor-not-allowed opacity-50"
                    disabled={true}
                  >
                    <Edit3 className="h-4 w-4" />
                  </Button>
                  <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 hidden group-hover:block bg-gray-800 text-white text-xs rounded py-1 px-2 z-10 whitespace-nowrap transition-opacity duration-200 opacity-0 group-hover:opacity-100">
                    Coming soon
                  </div>
                </div>
              </div>
            )}
          </div>
          
          {/* VERTICAL DIVIDER */}
          <div className="hidden sm:block w-px bg-gray-300 mx-2 self-stretch"></div>
          
          {/* DIRECTIONS SECTION */}
          <div className="min-w-0">
            <div className="text-xs font-semibold text-green-800 mb-1">DIRECTIONS</div>
            <div className="flex items-center">
              <Button
                type="button"
                size="sm"
                variant="outline"
                className={`h-7 px-3 text-xs font-medium border-2 ${groupDirectionsSet && groupDirectionsSet[group.id] ? 'bg-green-500 hover:bg-green-600 text-white border-green-600' : 'border-green-500 text-green-700 hover:bg-green-50'}`}
                onClick={toggleDirections}
              >
                {groupDirectionsSet && groupDirectionsSet[group.id] ? 'Edit' : 'Set'}
              </Button>
            </div>
          </div>
          
          {/* VERTICAL DIVIDER */}
          <div className="hidden sm:block w-px bg-gray-300 mx-2 self-stretch"></div>
          
          {/* TOTAL DURATION SECTION */}
          <div className="sm:min-w-[180px]">
            <div className="text-xs font-semibold text-purple-800 mb-1">TOTAL DURATION</div>
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center h-8">
                {loadingDuration ? (
                  <Skeleton className="h-6 w-16 rounded" />
                ) : (
                  <span className="font-mono text-sm bg-white px-2 py-1 rounded border text-purple-700 whitespace-nowrap">
                    {formatDuration(totalDuration)}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs font-medium text-gray-600">CONFIRM</span>
                <Button
                  type="button"
                  variant="outline"
                  onClick={toggleDurationConfirmation}
                  className={`h-6 w-6 p-0 rounded-full ${isDurationConfirmed ? 'bg-green-500 hover:bg-green-600 border-green-600' : 'hover:bg-gray-100 border-gray-300'}`}
                >
                  {isDurationConfirmed ? (
                    <Check className="h-4 w-4 text-white" />
                  ) : (
                    <div className="w-2 h-2 rounded-full bg-gray-400"></div>
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Road Detection Overlay */}
      {overlay}
    </>
  );
});

export default TimestampVerificationWidget;